import React, { useState, useEffect } from 'react';

// Validation libraries
import { useFormik } from 'formik';
import * as yup from 'yup';

// Hooks
import { useDispatch } from 'react-redux';

// Actions
import { success, failure } from '~/redux/actions/snackbar-actions';
import { updateCustomField } from '~/redux/actions/customFields-actions';

// Utils
import { MESSAGES } from '~/utils/message';
import { validateForm, realTimeValidation, resetValidation } from '~/utils/formik-validation';

// MUI Components
import Typography from '@mui/material/Typography';
import {
  Box,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

// Styles
import '../CreateModal/styles.scss';
import './styles.scss';
import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

const EditCustomFieldModal = (props) => {
  const formId = 'edit-custom-field-form';
  const {
    openModal,
    handleCloseModal,
    selectedCustomField,
    pagination,
    customFieldTypes = [],
  } = props;

  const [loading, setLoading] = useState(false);

  const onClose = () => {
    handleCloseModal();
    formik.handleReset();
    resetValidation();
    setLoading(false);
  };

  const dispatch = useDispatch();
  const validationSchema = yup.object().shape({
    name: yup
      .string()
      .trim()
      .required(MESSAGES.SETTINGS.CUSTOM_FIELDS.CUSTOM_FIELD_NAME_REQUIRED)
      .min(1, MESSAGES.SETTINGS.CUSTOM_FIELDS.CUSTOM_FIELD_NAME_REQUIRED)
      .max(25, MESSAGES.SETTINGS.CUSTOM_FIELDS.CUSTOM_FIELD_NAME_LIMIT)
      .matches(/^[a-zA-Z0-9 ]*$/, MESSAGES.SETTINGS.CUSTOM_FIELDS.CUSTOM_FIELD_VALIDATION),
    type: yup.string().required(MESSAGES.SETTINGS.CUSTOM_FIELDS.TYPE_REQUIRED),
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      type: 'single_text',
    },
    validationSchema: validationSchema,
  });

  const updateExistingCustomField = async (event) => {
    try {
      setLoading(true);
      event.preventDefault();
      const isValid = validateForm(formik);
      if (!isValid) {
        setLoading(false);
        return;
      }

      const payload = {
        customFieldName: formik.values.name,
        type: formik.values.type,
      };
      const response = await dispatch(
        updateCustomField(selectedCustomField.id, payload, pagination)
      );

      if (response?.status === 200) {
        dispatch(success(response.data?.message));
        onClose();
      } else if (
        response?.data?.message === MESSAGES.SETTINGS.CUSTOM_FIELDS.RESPONSE_ALREADY_EXIST
      ) {
        formik.setErrors({
          name: MESSAGES.SETTINGS.CUSTOM_FIELDS.FIELD_EXISTS,
        });
      } else {
        dispatch(failure(response?.data?.message || MESSAGES.GENERAL_ERROR));
      }
    } catch (error) {
      if (
        error?.response?.data?.message === MESSAGES.SETTINGS.CUSTOM_FIELDS.RESPONSE_ALREADY_EXIST
      ) {
        formik.setErrors({
          name: MESSAGES.SETTINGS.CUSTOM_FIELDS.FIELD_EXISTS,
        });
        return;
      }

      dispatch(failure(error?.response?.data?.message || error?.message || MESSAGES.GENERAL_ERROR));
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  useEffect(() => {
    if (selectedCustomField && openModal) {
      formik.resetForm({
        values: {
          name: selectedCustomField.value || '',
          type: selectedCustomField.type || 'single_text',
        },
      });
    }
  }, [selectedCustomField, openModal]);

  useEffect(() => {
    if (!openModal) {
      formik.handleReset();
      resetValidation();
      setLoading(false);
    }
  }, [openModal]);

  return (
    <OlcFormModal
      open={openModal}
      onClose={onClose}
      title={MESSAGES.SETTINGS.CUSTOM_FIELDS.EDIT.TITLE}
      className="customFieldFormModal"
      footer={
        <>
          <OlcButton variant="secondary" type="button" onClick={onClose} disabled={loading}>
            {MESSAGES.ACCOUNT.CANCEL_BUTTON}
          </OlcButton>
          <OlcButton variant="primary" type="submit" form={formId} disabled={loading}>
            {loading ? (
              <CircularProgress
                sx={{
                  color: 'white',
                  width: '20px !important',
                  height: '20px !important',
                }}
              />
            ) : (
              MESSAGES.SETTINGS.SUBMIT_BUTTON
            )}
          </OlcButton>
        </>
      }
    >
      <Box className="modalFormWrapper">
        <form id={formId} onSubmit={updateExistingCustomField} noValidate>
          <Box className="modalFlexArea">
            <Box>
              <InputLabel htmlFor="Custom Field" className="formLabel">
                {MESSAGES.SETTINGS.CUSTOM_FIELDS.EDIT.NAME}
              </InputLabel>
              <TextField
                placeholder="Type to update a Custom field"
                fullWidth
                name="name"
                className={
                  formik.errors.name && formik.touched.name
                    ? 'invalid formInput inputField formField'
                    : 'inputField formField'
                }
                onChange={formik.handleChange}
                value={formik.values.name?.trimStart()}
                onBlur={realTimeValidation ? formik.handleBlur : undefined}
                autoComplete="off"
                sx={{
                  '& input::placeholder': {
                    color: '#000',
                    fontSize: '14px',
                    fontWeight: '400',
                    lineHeight: 'normal',
                    fontFamily: 'Inter',
                    fontStyle: 'normal',
                  },
                  outline: 'none',
                  '&:focus': {
                    outline: 'none',
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                      {formik.values.name && (
                        <IconButton
                          className="cpClearInputBtn"
                          size="small"
                          onClick={() => {
                            formik.setFieldValue('name', '');
                            formik.setFieldTouched('name', true);
                          }}
                          edge="end"
                          sx={{ padding: '4px' }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              {formik.touched.name && formik.errors.name ? (
                <Typography className="errorMessage">
                  <sup>*</sup>
                  {formik.errors.name}
                </Typography>
              ) : null}
            </Box>
            <Box>
              <InputLabel htmlFor="type" className="formLabel">
                {MESSAGES.SETTINGS.CUSTOM_FIELDS.TYPE}
                <span className="olcRequired">*</span>
              </InputLabel>
              <FormControl fullWidth>
                <Select
                  id="type"
                  name="type"
                  value={formik.values.type}
                  onChange={formik.handleChange}
                  onBlur={realTimeValidation ? formik.handleBlur : undefined}
                  className="inputField formField customFieldTypeSelect"
                  MenuProps={{
                    PaperProps: {
                      className: 'customFieldTypeMenu',
                    },
                  }}
                >
                  {customFieldTypes.map((fieldType) => (
                    <MenuItem key={fieldType.key} value={fieldType.key}>
                      {fieldType.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {formik.errors.type ? (
                <Typography className="errorMessage">
                  <sup>*</sup>
                  {formik.errors.type}
                </Typography>
              ) : null}
            </Box>
          </Box>
        </form>
      </Box>
    </OlcFormModal>
  );
};

export default EditCustomFieldModal;
